import {
  collectVariableInfo,
  generateVariablesSection,
  updateFilesWithVariables,
} from "./processVariables";
import {
  getFilenameFromPath,
  formatHtml,
  calculateTokenCounts,
  convertRichTextToHtml,
  cleanJsonString,
} from "./previewMisc";
import { processAllFiles, enhanceFilesWithPromptInfo } from "./processFile";
import { buildFragmentMap } from "./processFragments";
import {
  categorizePromptPaths,
  getCachedChatCompletion,
  getSortedPromptNames,
} from "./processPrompts";
import { getRequiredVariables } from "../../services/promptService";
import { renderAndHighlightTemplate } from "../../services/promptService";

const CSS_CLASSES = {
  REPORT_SECTION: "report-section",
  PERSONA_INFO: "persona-info",
  PERSONA_NAME: "persona-name",
  ERROR_SECTION: "error-section",
  TOKEN_COUNT_TOTAL: "token-count-total",
  TOKEN_COUNT_NUMBER: "token-count-number",
  TOKEN_COUNT_NOTE: "token-count-note",
  SUCCESS_TEXT: "success-text",
};

const HTML_CLASSES = {
  AI_RESPONSE: "ai-response",
  AI_RESPONSE_ERROR: "ai-response error",
  AI_RESPONSE_SKIPPED: "ai-response skipped",
  TEXT_RESPONSE: "text-response",
  JSON_RESPONSE: "json-response",
  TOKENS: "ai-response-tokens",
  NOTE: "ai-response-note",
};

const generatePersonaSection = (configFile) => {
  if (configFile?.persona) {
    return `<div class="${CSS_CLASSES.PERSONA_INFO}">Using persona: <span class="${CSS_CLASSES.PERSONA_NAME}">${configFile.persona}</span></div>`;
  }
  return `<div class="${CSS_CLASSES.ERROR_SECTION}"><p>⚠️ No persona configured in launch configuration</p></div>`;
};

const generateTokenCountSection = (
  formattedAdjustedTotalTokenCount,
  adjustedTotalTokenCount,
  totalTokenCount
) => {
  let tokenCountHtml = `<div class="${CSS_CLASSES.TOKEN_COUNT_TOTAL}">Total token count: <span class="${CSS_CLASSES.TOKEN_COUNT_NUMBER}">${formattedAdjustedTotalTokenCount}</span> tokens</div>`;

  if (adjustedTotalTokenCount !== totalTokenCount) {
    tokenCountHtml += `<div class="${CSS_CLASSES.TOKEN_COUNT_NOTE}">* Token count excludes prompts that skip the LLM</div>`;
  }

  return tokenCountHtml;
};

const generateFragmentsSection = (promisedVersions) => {
  if (promisedVersions.length === 0) {
    return "<p><i>No fragments configured</i></p>";
  }

  return formatHtml.ul(
    promisedVersions.map(
      (fragment) =>
        `${formatHtml.span(
          CSS_CLASSES.SUCCESS_TEXT,
          fragment.block_name
        )} - Version: ${fragment.version}`
    )
  );
};

const generateFinalPreview = (params) => {
  const {
    processedFiles,
    configFile,
    formattedAdjustedTotalTokenCount,
    adjustedTotalTokenCount,
    totalTokenCount,
    promisedVersions,
    variableInfo,
    aiResponses,
  } = params;
  const headerContent = `
${formatHtml.h1("Prompt Preview Report")}
${formatHtml.p(
  "This is a consolidated preview of all prompt templates with fragments and variables replaced."
)}
${generatePersonaSection(configFile)}
${generateTokenCountSection(
  formattedAdjustedTotalTokenCount,
  adjustedTotalTokenCount,
  totalTokenCount
)}`;

  const fragmentsContent = `
${formatHtml.h1("Fragments Summary")}
${generateFragmentsSection(promisedVersions)}`;

  const variablesSection = formatHtml.section(
    CSS_CLASSES.REPORT_SECTION,
    generateVariablesSection(variableInfo)
  );

  const preprocessingSection = generatePromptSectionHTML(
    processedFiles.preprocessing,
    "Pre-processing Templates",
    aiResponses
  );

  const primarySection = generatePromptSectionHTML(
    processedFiles.primary,
    "Primary Templates",
    aiResponses
  );

  const postprocessingSection = generatePromptSectionHTML(
    processedFiles.postprocessing,
    "Post-processing Templates",
    aiResponses
  );

  const voicemailSection = generatePromptSectionHTML(
    processedFiles.voicemail,
    "Voicemail Templates",
    aiResponses
  );

  return `
<div class="${CSS_CLASSES.REPORT_SECTION}">${headerContent}</div>
<div class="${CSS_CLASSES.REPORT_SECTION}">${fragmentsContent}</div>
${variablesSection}
${preprocessingSection}
${primarySection}
${postprocessingSection}
${voicemailSection}
`;
};

const generatePlainTextPreview = (processedFiles) => {
  return [
    ...processedFiles.preprocessing.map((f) => f.plainText),
    ...processedFiles.primary.map((f) => f.plainText),
    ...processedFiles.postprocessing.map((f) => f.plainText),
    ...processedFiles.voicemail.map((f) => f.plainText),
  ].join("\n\n");
};

const generatePromptHeader = (file) => {
  if (!file || !file.path) return formatHtml.h2("Unknown file");

  const filename = getFilenameFromPath(file.path);
  const fileInfo = file.promptInfo || {};

  const tokenCountDisplay = file.llmSkipped
    ? formatHtml.span(
        "token-count skipped",
        "LLM Skipped",
        `Original token count: ${file.originalTokenCount || "unknown"}`
      )
    : formatHtml.span("token-count", `${file.formattedTokenCount || 0} tokens`);

  const typeElements = [];

  if (fileInfo.type) {
    const typeClass = `prompt-type type-${fileInfo.type}`;
    typeElements.push(
      formatHtml.span(typeClass, fileInfo.typeLabel || fileInfo.type)
    );
  }

  if (fileInfo.skill && fileInfo.skill !== "disabled") {
    const skillClass = `prompt-skill skill-${fileInfo.skill}`;
    typeElements.push(
      formatHtml.span(
        skillClass,
        fileInfo.skillLabel || fileInfo.skill,
        fileInfo.skillTooltip
      )
    );
  }

  if (fileInfo.skipLlm) {
    typeElements.push(
      formatHtml.span(
        "prompt-skip-llm",
        "Skip LLM",
        "This prompt skips the LLM and won't consume tokens"
      )
    );
  }

  const typeInfo = typeElements.join(" ");

  const filenameSection = formatHtml.span("prompt-filename", filename);
  const tokenCountSection = formatHtml.span(
    "prompt-token-count",
    tokenCountDisplay
  );
  const typeInfoSection = typeInfo
    ? `<div class="prompt-info">${typeInfo}</div>`
    : "";

  return formatHtml.h2(filenameSection + tokenCountSection + typeInfoSection);
};

const generatePromptSectionHTML = (files, sectionTitle, aiResponses) => {
  if (!Array.isArray(files) || files.length === 0) return "";

  const filesSections = files.map((file) => {
    if (!file) return "";

    const processedRichText = file.richText
      ? convertRichTextToHtml(file.richText)
      : "<i>No preview available</i>";

    return `
${generatePromptHeader(file)}
${formatHtml.h3("Fragments")}
${file.fragmentReport || "<i>No fragments</i>"}
${formatHtml.h3("Variables")}
${file.variableReport || "<i>No variables</i>"}
${generateToolsSection(file)}
${formatHtml.h3("Preview")}
<div class="template-preview">
${processedRichText}
</div>
${
  sectionTitle === "Pre-processing Templates" && aiResponses
    ? generateAiResponseSection(file, aiResponses)
    : ""
}
`;
  });

  return `<div class="report-section">
${formatHtml.h3(sectionTitle) || "Templates"}
${filesSections.join("\n\n")}
</div>`;
};

const generateToolsSection = (file) => {
  if (!file)
    return formatHtml.h3("Tools") + "\n<i>No tools information available</i>\n";

  const fileInfo = file.promptInfo;

  if (
    !fileInfo ||
    !fileInfo.tools ||
    !Array.isArray(fileInfo.tools) ||
    fileInfo.tools.length === 0
  ) {
    return `${formatHtml.h3("Tools")}\n<i>No tools configured</i>\n`;
  }

  const toolItems = Array.isArray(fileInfo.toolDetails)
    ? fileInfo.toolDetails.map((tool) => {
        if (!tool) return "";

        const label = tool.label || tool.name || "Unknown tool";
        const description = tool.description ? ` - ${tool.description}` : "";
        return `${formatHtml.span("tool-name", label)}${description}`;
      })
    : [];

  return `${formatHtml.h3("Tools")}\n${formatHtml.ul(toolItems)}\n`;
};

const generatePreprocessingPrompts = async (
  preprocessingFiles,
  configuredVariables,
  modelName
) => {
  const configuredVariablesCopy = { ...configuredVariables };

  if (!Array.isArray(preprocessingFiles)) {
    throw new Error("preprocessingFiles must be an array");
  }

  const sortedFiles = [...preprocessingFiles].sort((a, b) =>
    (a.path || "").localeCompare(b.path || "")
  );

  const responses = {};
  const promptOutputs = {};
  const errors = [];
  const llmRequests = [];

  for (const file of sortedFiles) {
    if (!file || !file.path) {
      errors.push("Encountered invalid file entry in preprocessingFiles");
      continue;
    }

    const promptName = getFilenameFromPath(file.path);
    let promptContent = file.plainText || "";
    let promptSkill = file.promptInfo.skill;

    if (!promptContent) {
      const error = `Empty prompt content for ${promptName}`;
      errors.push(error);
      responses[promptName] = { error, content: null };
      continue;
    }

    try {
      const initialContent = promptContent;
      const tools = file.promptInfo?.tools || [];
      const res = await renderAndHighlightTemplate(
        promptContent,
        configuredVariablesCopy,
        promptOutputs,
        tools,
        false
      );
      promptContent = res.rendered_prompt;
      const unconfiguredVars = res.unconfigured_variables || [];
      const specialVars = res.special_variables || [];

      if (unconfiguredVars.length > 0) {
        console.warn(
          `Unconfigured variables in prompt ${promptName}: ${unconfiguredVars.join(
            ", "
          )}. Unable to replace these variables.`
        );
        responses[promptName] = {
          error: `Unconfigured variables: ${unconfiguredVars.join(", ")}`,
          content: initialContent,
        };
        promptOutputs[promptName] = initialContent;
        continue;
      } else if (specialVars.length > 0) {
        console.warn(
          `Special variables in prompt ${promptName}: ${specialVars.join(
            ", "
          )}. These variables may not be replaced correctly.`
        );
        responses[promptName] = {
          error: `Unable to render special vars: ${unconfiguredVars.join(
            ", "
          )}`,
          content: initialContent,
        };
        promptOutputs[promptName] = initialContent;
        continue;
      }
    } catch (error) {
      const errorMessage = `Error replacing variables in ${promptName}: ${error.message}`;
      errors.push(errorMessage);
      responses[promptName] = { error: errorMessage, content: null };
      continue;
    }

    if (file.promptInfo?.skipLlm) {
      responses[promptName] = {
        content: promptContent,
        skippedLlm: true,
      };

      promptOutputs[promptName] = promptContent;
      continue;
    }

    llmRequests.push({
      promptName,
      promptSkill,
      params: {
        messages: [{ role: "user", content: promptContent }],
        model: modelName || "gpt-4",
        max_tokens: 1000,
        temperature: 0,
        response_format: "text",
      },
    });
  }

  await Promise.all(
    llmRequests.map(async (request) => {
      try {
        const response = await getCachedChatCompletion(request.params);

        if (!response || !response.data) {
          throw new Error("Empty response received from LLM service");
        }

        const responseContent =
          response.data?.choices?.[0]?.message?.content || "";
        const responseTokens = response.data?.usage?.total_tokens || 0;
        const responseModel =
          response.data?.model || request.params.model || "AI";

        responses[request.promptName] = {
          content: responseContent,
          tokens: responseTokens,
          model: responseModel,
        };
        if (request.promptSkill === "SetVariable") {
          const cleanedContent = cleanJsonString(responseContent);
          const updatedVariables = JSON.parse(cleanedContent);

          Object.keys(updatedVariables).forEach((key) => {
            configuredVariablesCopy[key] = updatedVariables[key];
          });
        }

        promptOutputs[request.promptName] = responseContent;
      } catch (error) {
        const errorMessage = `Error calling AI for ${request.promptName}: ${error.message}`;
        errors.push(errorMessage);

        responses[request.promptName] = {
          error: errorMessage,
          content: null,
          details: {
            timestamp: new Date().toISOString(),
            promptName: request.promptName,
            errorType: error.name || "UnknownError",
            statusCode: error.response?.status || "N/A",
            isNetworkError: error.isAxiosError || false,
          },
        };

        promptOutputs[request.promptName] = "";
      }
    })
  );

  return {
    responses,
    updatedVariables: configuredVariablesCopy,
    promptOutputs,
    errors: errors.length > 0 ? errors : null,
  };
};

export const generateConsolidatedPromptPreview = async ({
  promptKey,
  selectedVersion,
  contents,
  configFile,
  promptFragments,
  getFragmentVersionsById,
  calculateTokenCount,
  promptProperties = {},
  generativeModel = null,
}) => {
  try {
    const { fragmentMap, promisedVersions } = await buildFragmentMap({
      promptFragments,
      getFragmentVersionsById,
    }).catch((error) => {
      console.error("Error building fragment map:", error);
      throw new Error("Failed to build fragment map");
    });

    const promptPaths = categorizePromptPaths(contents);

    const processedFiles = await processAllFiles(
      promptPaths,
      { contents, configFile, calculateTokenCount },
      fragmentMap,
      promptKey,
      selectedVersion
    ).catch((error) => {
      console.error("Error processing files:", error);
      throw new Error("Failed to process prompt files");
    });

    const { allPromptNames } = enhanceFilesWithPromptInfo(
      processedFiles,
      promptProperties
    );

    const sortedPromptNames = getSortedPromptNames(
      allPromptNames,
      processedFiles
    );

    const { aiResponses, updatedVars, promptOutputs, aiCallsSuccessful } =
      await generateAiResponses(
        processedFiles,
        configFile,
        generativeModel
      ).catch((error) => {
        console.error("Error processing AI responses:", error);
        throw new Error("Failed to process AI responses");
      });

    await updateFilesWithVariables(
      processedFiles,
      updatedVars,
      sortedPromptNames,
      promptOutputs,
      calculateTokenCount,
      fragmentMap
    ).catch((error) => {
      console.error("Error updating files with variables:", error);
      throw new Error("Failed to update files with variables");
    });

    const variableInfo = collectVariableInfo(
      processedFiles,
      updatedVars,
      fragmentMap
    );

    variableInfo.promptOutputs = promptOutputs;

    const {
      totalTokenCount,
      adjustedTotalTokenCount,
      formattedAdjustedTotalTokenCount,
    } = calculateTokenCounts(processedFiles);

    const requiredVariables = await getRequiredVariables({
      topicKey: promptKey,
      version: selectedVersion,
    }).catch((error) => {
      console.error("Error fetching required variables:", error);
      throw new Error("Failed to fetch required variables");
    });
    const sortedRequiredVariables = requiredVariables.sort();
    variableInfo.requiredVariables = sortedRequiredVariables;

    const finalPreview = generateFinalPreview({
      processedFiles,
      configFile,
      formattedAdjustedTotalTokenCount,
      adjustedTotalTokenCount,
      totalTokenCount,
      promisedVersions,
      variableInfo,
      aiResponses,
    });

    const plainTextPreview = generatePlainTextPreview(processedFiles);

    return {
      finalPreview,
      plainTextPreview,
      adjustedTotalTokenCount,
      unconfiguredVariablesCount: variableInfo.unconfiguredVariables.length,
      aiCallsSuccessful,
      requiredVariables,
    };
  } catch (error) {
    console.error("Error generating consolidated prompt preview:", error);
    throw error;
  }
};

const generateAiResponseSection = (file, aiResponses) => {
  const filename = file.path.split("/").pop();
  const response = aiResponses[filename];

  if (!response) {
    return "";
  }

  if (response.skippedLlm) {
    return (
      formatHtml.h3("AI Response (LLM Skipped)") +
      formatHtml.div(
        HTML_CLASSES.AI_RESPONSE_SKIPPED,
        formatHtml.div(
          HTML_CLASSES.NOTE,
          "LLM was skipped for this prompt. Raw prompt content is used as output."
        ) +
          formatHtml.div(
            HTML_CLASSES.TEXT_RESPONSE,
            response.content.replace(/\n/g, formatHtml.br())
          )
      )
    );
  }

  if (response.error) {
    return (
      formatHtml.h3("AI Response (Error)") +
      formatHtml.div(
        HTML_CLASSES.AI_RESPONSE_ERROR,
        formatHtml.div("ai-response-error", response.error)
      )
    );
  }

  let formattedResponse = "";
  try {
    if (
      response.content.trim().startsWith("{") &&
      response.content.trim().endsWith("}")
    ) {
      try {
        const parsedJson = JSON.parse(response.content);
        formattedResponse = formatHtml.pre(
          HTML_CLASSES.JSON_RESPONSE,
          JSON.stringify(parsedJson, null, 2)
        );
      } catch (parseError) {
        console.error("Failed to parse JSON response:", parseError);
        formattedResponse = formatHtml.div(
          HTML_CLASSES.TEXT_RESPONSE,
          `${response.content.replace(/\n/g, formatHtml.br())}` +
            formatHtml.div(
              "parse-error",
              `(Note: Content appeared to be JSON but failed to parse: ${parseError.message})`
            )
        );
      }
    } else {
      formattedResponse = formatHtml.div(
        HTML_CLASSES.TEXT_RESPONSE,
        response.content.replace(/\n/g, formatHtml.br())
      );
    }
  } catch (e) {
    console.error("Error formatting AI response:", e);
    formattedResponse = formatHtml.div(
      HTML_CLASSES.TEXT_RESPONSE,
      response.content.replace(/\n/g, formatHtml.br())
    );
  }

  const modelName = response.model || "AI";
  const tokensInfo = `${response.tokens || "Unknown"} tokens used`;
  const variableNote = `This output can be used in other prompts via ${formatHtml.code(
    `{{ ${filename} }`
  )}`;

  return (
    formatHtml.h3(`AI Response (${modelName})`) +
    formatHtml.div(
      HTML_CLASSES.AI_RESPONSE,
      formatHtml.div(HTML_CLASSES.TOKENS, tokensInfo) +
        formatHtml.div(HTML_CLASSES.NOTE, variableNote) +
        formattedResponse
    )
  );
};

const generateAiResponses = async (
  processedFiles,
  configFile,
  generativeModel
) => {
  let aiResponses = {};
  let promptOutputs = {};
  let aiCallsSuccessful = true;
  let updatedVars = {};

  const { persona, variables = {} } = configFile;

  if (persona && processedFiles.preprocessing.length > 0 && generativeModel) {
    try {
      const {
        responses,
        updatedVariables,
        promptOutputs: outputs,
        errors,
      } = await generatePreprocessingPrompts(
        processedFiles.preprocessing,
        variables,
        generativeModel
      );
      aiResponses = responses;
      updatedVars = updatedVariables;
      promptOutputs = outputs;
      aiCallsSuccessful = true;
    } catch (error) {
      console.error("Error processing AI responses:", error);
      updatedVars = { ...variables };
      aiCallsSuccessful = false;
    }
  } else {
    updatedVars = { ...variables };
  }
  if (!variables.modality && configFile.modality) {
    updatedVars.modality = configFile.modality;
  }

  return { aiResponses, updatedVars, promptOutputs, aiCallsSuccessful };
};
